import { Component, computed, DestroyRef, inject, input, signal, TemplateRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule, UntypedFormGroup, Validators } from '@angular/forms';
import { catchError, debounceTime, distinctUntilChanged, EMPTY, filter, map, switchMap, tap } from 'rxjs';
import { SearchDtoBase } from '@myarvato/shared/models';
import { NavigationStart, Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { TableModule } from 'primeng/table';
import { InputTextModule } from 'primeng/inputtext';
import { DynamicDialogRef } from 'primeng/dynamicdialog';
import { UserTrackingService } from '@myarvato/core/services';
import { BaseSearchService } from '../services/base-search.service';
import { SearchbarComponent } from '@myarvato/components/searchbar';
import { ButtonModule } from 'primeng/button';
import { ErrorMessageComponent } from '@myarvato/components/message';

@Component({
  selector: 'myarvato-search-widget',
  templateUrl: './search-widget.component.html',
  styleUrl: './search-widget.component.scss',
  imports: [
    ButtonModule,
    NgTemplateOutlet,
    FormsModule,
    ReactiveFormsModule,
    InputTextModule,
    TableModule,
    TranslateModule,
    SearchbarComponent,
    ErrorMessageComponent,
  ],
  host: {
    '(keydown.ArrowUp)': 'arrowUp($event)',
    '(keydown.ArrowDown)': 'arrowDown($event)',
    '(keydown.Enter)': 'enter($event)',
  },
})
export class SearchWidgetComponent {
  public readonly rowTemplate = input.required<TemplateRef<{ $implicit: SearchDtoBase }>>();
  public readonly translationKey = input.required<string>();
  public readonly trackerCategory = input.required<string>();
  public readonly maxSearchLength = input<number | null>(null);

  // Fields
  protected readonly loading = signal(false);
  protected readonly results = signal<SearchDtoBase[]>([]);
  protected readonly selected = signal<SearchDtoBase | undefined>(undefined);
  protected readonly errorMessage = signal('');
  protected readonly searchInputControl = new FormControl(undefined, [Validators.required]);
  protected searchForm = new UntypedFormGroup({
    searchInput: this.searchInputControl,
  });

  protected readonly hasResults = computed(() => this.results().length > 0);
  protected readonly searchResultsTotalCount = computed(() => this.searchService.searchResultTotalCount());

  // Injections
  private readonly destroyRef = inject(DestroyRef);
  private readonly dynamicDialogRef = inject(DynamicDialogRef);
  private readonly searchService = inject(BaseSearchService);
  private readonly tracker = inject(UserTrackingService);
  private readonly translateService = inject(TranslateService);
  private readonly rowIndex = signal(0);

  constructor() {
    const router = inject(Router);

    router.events
      .pipe(
        filter((event) => event instanceof NavigationStart && event.navigationTrigger === 'popstate'),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.close());

    this.handleSearchTermChanges();
  }

  protected close(): void {
    this.closeWith(this.selected());
  }

  protected arrowUp(event: Event) {
    event.preventDefault();
    if (this.rowIndex() > 0) {
      this.rowIndex.update((i) => i - 1);
    }
    this.selected.set(this.results()[this.rowIndex()]);
  }

  protected arrowDown(event: Event) {
    event.preventDefault();
    if (this.rowIndex() < this.results().length - 1) {
      this.rowIndex.update((i) => i + 1);
    }
    this.selected.set(this.results()[this.rowIndex()]);
  }

  protected enter(event: Event) {
    event.preventDefault();
    const current = this.selected();
    if (current) {
      this.closeWith(current);
    }
  }

  protected clearSearch() {
    this.searchForm.patchValue({ searchInput: '' });
    this.rowIndex.set(0);
    this.results.set([]);
  }

  protected mouseClick(item: SearchDtoBase) {
    const index = this.results().indexOf(item);
    if (index !== -1) {
      this.rowIndex.set(index);
      this.selected.set(item);
      this.tracker.trackEvent(this.trackerCategory(), 'Search selected');
      this.closeWith(item);
    }
  }

  /** Closes whichever dialog type is currently hosting this component. */
  private closeWith(result?: SearchDtoBase): void {
    this.dynamicDialogRef.close(result);
  }

  private handleSearchTermChanges(): void {
    this.searchForm.valueChanges
      .pipe(
        debounceTime(700),
        distinctUntilChanged(),
        filter((formValue) => formValue.searchInput.trim().length > 2),
        map((formValue) => formValue.searchInput as string),
        tap(() => this.loading.set(true)),
        switchMap((term) =>
          this.searchService.getSearchIndex(term.trim()).pipe(
            tap((data: SearchDtoBase[]) => {
              data.forEach((element) => {
                const key = this.searchService.findSearchField(element, term);
                if (key) {
                  element.searchKey = key.propertyName;
                  element.highlightedValue = key.highlightedValue;
                } else {
                  // Fallback: when highlighting is not possible (e.g., concatenated/indexed fields)
                  element.searchKey = 'arvatoOrderNo';
                  element.highlightedValue = element.arvatoOrderNo;
                }
              });

              this.results.set(data);
              this.selected.set(data[0]);
              this.errorMessage.set('');
              this.loading.set(false);
            }),
            catchError((errObj) => {
              console.error(errObj);
              this.results.set([]);
              this.selected.set(undefined);
              this.loading.set(false);

              if (errObj.status === 404) {
                this.errorMessage.set('');
              } else {
                this.errorMessage.set(
                  this.translateService.instant('search-dialog.error-while-searching', {
                    error: errObj.error?.message || this.translateService.instant('search-dialog.unknown-error'),
                  }),
                );
              }
              return EMPTY;
            }),
          ),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe();
  }
}
